
import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import FileUpload from '@/util/file-upload';
import Vue from 'vue';
import * as Sentry from '@sentry/browser';
import dayjs from "@/util/dayjs";
import TransferInteractionConfiguration from "@/models/TransferInteractionConfiguration";

enum UploadFormState {
    FORM = 1,
    PROGRESS = 2,
    SUCCESS = 3,
    ERROR = 4,
}

@Module
export default class UploadStore extends VuexModule {
    status: UploadFormState = UploadFormState.FORM;
    progress: number = 0;
    handle: FileUpload | null = null;
    files: File[] = [];
    usesPassword: boolean = false;
    password: string = '';
    error: string = '';
    linkForPartner: string = '';
    metaKey: CryptoKey | null = null;
    passwordSalt: Uint8Array | null = null;
    uploadProgressPerFile: object = {};
    overallProgress: number = 0;
    currentlyUploadingFile: string = "";
    uploadExpiration: Date = dayjs().add(14, 'days').endOf('day').toDate(); //Default: 2 Weeks!
    allowComments: boolean = false;
    enableApproval: boolean = false;
    requireName: boolean = false;

    get hasUploadFile() {
        return this.files.length > 0;
    }

    get showUploadForm() {
        return this.status === UploadFormState.FORM;
    }

    get showUploadProgress() {
        return this.status === UploadFormState.PROGRESS;
    }

    get showUploadSuccess() {
        return this.status === UploadFormState.SUCCESS;
    }

    get showUploadError() {
        return this.status === UploadFormState.ERROR;
    }

    @Mutation
    setMetaKey(metaKey: CryptoKey) {
        this.metaKey = metaKey;
    }

    @Mutation
    setPasswordSalt(passwordSalt: Uint8Array) {
        this.passwordSalt = passwordSalt;
    }

    @Mutation
    startUpload() {
        if (this.files.length > 0 && this.metaKey) {
            const config: TransferInteractionConfiguration = new TransferInteractionConfiguration();
            config.enableComments = this.allowComments ? 'twoway' : 'disabled';
            config.approvalEnabled = this.enableApproval;
            config.needsNameEntered = this.requireName;
            this.handle = new FileUpload(this.files, this.uploadExpiration, config, this.metaKey);
            this.uploadProgressPerFile = {};
            this.error  = '';
            this.status = UploadFormState.PROGRESS;
            this.overallProgress = 0;
            this.currentlyUploadingFile = "";
        }
    }

    @Mutation
    setUploadDone(link: string) {
        this.status = UploadFormState.SUCCESS;
        this.linkForPartner = link;
        this.uploadProgressPerFile = {};
        this.error = '';
        this.overallProgress = 100;
        this.currentlyUploadingFile = "";
    }

    @Mutation
    failUpload(errorMessage: string) {
        this.status = UploadFormState.ERROR;
        this.uploadProgressPerFile = {};
        this.error = errorMessage;
        this.overallProgress = 0;
        this.currentlyUploadingFile = "";
    }

    @Mutation
    setUploadPassword(password: string) {
        this.password = password;
    }

    @Mutation
    setUploadUsesPassword(usesPassword: boolean) {
        this.usesPassword = usesPassword;
    }

    @Mutation
    addUploadFile(file: File) {
        this.files.push(file);
    }

    @Mutation
    removeUploadFile(file: File) {
        const idx = this.files.findIndex((f: File) => {
            return f.name === file.name && f.size === f.size;
        });
        if (idx > -1) {
            this.files.splice(idx, 1);
        }
    }

    @Mutation
    clearUploadFile() {
        this.files = [];
        this.metaKey = null;
    }

    @Mutation
    startOverWithUpload() {
        this.status = UploadFormState.FORM;
        this.files = [];
        this.uploadProgressPerFile = {};
        this.error = '';
        this.currentlyUploadingFile = "";
    }

    @Mutation
    setUploadProgressForFile(payload: {fileName: string, percentDone: number}) {
        Vue.set(this.uploadProgressPerFile, payload.fileName, payload.percentDone);
    }

    @Mutation
    setOverallProgress(overallProgress: number) {
        this.overallProgress = overallProgress;
    }

    @Mutation
    setCurrentUploadingFile(filename: string) {
        this.currentlyUploadingFile = filename;
    }

    @Mutation
    setUploadExpiration(date: Date) {
        this.uploadExpiration = date;
    }

    @Action({ rawError: true })
    async uploadMetadata(letterboxAddress: string | null) {
        if (this.files.length > 0 && this.metaKey) {
            try {
                await this.context.commit('startUpload');
                Vue.$log.debug("Starting upload");
                if (this.handle) { //always true after startUpload
                    const salt = this.passwordSalt; //Ensure this is at least here
                    return await this.handle.startUpload(this.usesPassword, this.password, salt, letterboxAddress);
                }
            } catch (e) {
                this.context.commit('failUpload', 'Ein Fehler beim Hochladen trat auf.');
                Vue.$log.error("Uploading metadata failed", e);
                Sentry.captureException(e);
            }
        } else {
            Vue.$log.debug("Preconditions not fulfilled");
        }
    }

    @Action({ rawError: true })
    async uploadChunks() {
        if (this.handle && this.files.length > 0 && this.status === UploadFormState.PROGRESS) {
            try {
                const result = await this.handle.uploadChunks((fileName, filePercent, totalPercent, progressEvent) => {
                    this.context.commit('setOverallProgress', totalPercent);
                    this.context.commit('setUploadProgressForFile', {fileName, filePercent});
                    this.context.commit('setCurrentUploadingFile', fileName);
                });
                return result;
            } catch (e) {
                this.context.commit('failUpload', e.message);
                Vue.$log.error("Uploading chunks failed", e);
                Sentry.captureException(e);
            }
        } else {
            Vue.$log.debug("Preconditions not fulfilled");
        }
    }

    @Action({ rawError: true })
    async commitUpload(neededForDecryption: string) {
        if (this.handle && this.status === UploadFormState.PROGRESS) {
            try {
                let fileLink: string = await this.handle.finishUpload();
                fileLink +=  '#' + encodeURIComponent(neededForDecryption);
                this.context.commit('setUploadDone', fileLink);
                return fileLink;
            } catch (e) {
                this.context.commit('failUpload', 'Ein Fehler beim Hochladen trat auf.');
                Vue.$log.error("Committing upload failed", e);
                Sentry.captureException(e);
            }
        } else {
            Vue.$log.debug("Preconditions not fulfilled");
        }
    }
}
